import { useEffect, useRef, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { CARD, DECOR, DECOR_COLORS, Theme, ThemeColors } from "./login-theme";

interface WelcomeProps extends WithTranslation {
  theme: Theme;
  colors: ThemeColors;
  // 'stage'：登录页缩放画布内、按设计稿 1:1 绝对定位的卡片（默认）。
  // 'panel'：注册/找回密码等旧版 grid 布局里，作为填充左列的等比缩放面板。
  variant?: 'stage' | 'panel';
}

// 卡片内容（装饰 + 品牌 + Hero），坐标均相对卡片左上角，stage / panel 两种形态复用。
function CardContent({ t, theme }: { t: WelcomeProps['t']; theme: Theme }) {
  return (
    <>
      {/* 装饰插画（位于文字下层）：单色形状用 CSS mask 上色，颜色按主题查表。
          几何只保留 1 份去色 SVG（deco-{key}.svg），主题色由 backgroundColor 提供。 */}
      {DECOR.map((d) => {
        const mask = `url(/images/login/deco-${d.key}.svg) no-repeat center / 100% 100%`;
        return (
          <div
            key={d.key}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: d.left,
              top: d.top,
              width: d.width,
              height: d.height,
              backgroundColor: DECOR_COLORS[theme][d.key],
              WebkitMask: mask,
              mask,
            }}
          />
        );
      })}

      {/* 品牌区：logo + 易學平台 （设计稿 frame(94,86) → 卡片内(60,58)） */}
      <div style={{ position: 'absolute', left: 60, top: 58, display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/images/login/logo.png" alt="logo" style={{ height: 54, width: 'auto' }} draggable={false} />
        {/* 品牌名固定使用繁体中文，不随语言切换翻译。
            设计稿品牌字体为 Inter 600；Inter 无中文字形，故中文回退到 MiSans。 */}
        <span style={{ fontFamily: '"Inter", "MiSans", system-ui, sans-serif', fontSize: 28, fontWeight: 600, color: '#000', letterSpacing: '-0.01em' }}>
          易學平台
        </span>
      </div>

      {/* Hero 标题（设计稿 frame(98,318) → 卡片内(64,290)，72px） */}
      <h1
        style={{
          position: 'absolute',
          left: 64,
          top: 290,
          margin: 0,
          fontSize: 72,
          fontWeight: 500,
          lineHeight: '72px',
          color: '#000',
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        {t("welcome.hero-line-1")}
        <br />
        {t("welcome.hero-line-2")}
      </h1>
    </>
  );
}

// panel 形态：旧版 grid 页面（注册 / 找回密码 / 重置密码）左列展示同一张卡片，
// 按列宽等比缩放（contain）后居中。仅 ≥lg 显示（与旧 Welcome 的 hidden lg:* 行为一致）。
function WelcomePanel({ t, theme, colors }: { t: WelcomeProps['t']; theme: Theme; colors: ThemeColors }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(Math.min(el.clientWidth / CARD.width, el.clientHeight / CARD.height));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative hidden lg:block overflow-hidden" style={{ minHeight: '100svh' }}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transformOrigin: 'center',
          transform: `translate(-50%, -50%) scale(${scale})`,
          width: CARD.width,
          height: CARD.height,
          borderRadius: CARD.radius,
          background: colors.cardBg,
          overflow: 'hidden',
          visibility: scale ? 'visible' : 'hidden',
        }}
      >
        <CardContent t={t} theme={theme} />
      </div>
    </div>
  );
}

// 左侧大卡片：品牌 + Hero 标题 + 装饰插画（按设计稿 1:1 绝对定位）
function Welcome({ t, theme, colors, variant = 'stage' }: WelcomeProps) {
  if (variant === 'panel') {
    return <WelcomePanel t={t} theme={theme} colors={colors} />;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: CARD.left,
        top: CARD.top,
        width: CARD.width,
        height: CARD.height,
        borderRadius: CARD.radius,
        background: colors.cardBg,
        overflow: 'hidden',
      }}
    >
      <CardContent t={t} theme={theme} />
    </div>
  );
}

export default withTranslation()(Welcome);
